import { createSelector } from '@reduxjs/toolkit'

import { normalizeRid } from '../../../shared/model/scopeKeys'
import { warehousePickOf, type StructureResult } from '../../structure/model/selectors'
import { formatMoney } from '../../pricing/model/format'
import { DAY_MS, EMPTY_NAME_TEXT, NO_PRICE_TEXT } from './constants'
import { IDLE_PRICE_TAG_SCAN } from './priceTagsSlice'
import { LABEL_TEMPLATES, MAX_LABEL_COPIES, templateOf } from './templates'
import type { PriceTagItem, PriceTagScanState, PriceTagSelection, PriceTagsState, PriceTagsVm } from './types'

export interface PriceTagsVmInput {
  resourceId: string
  tags: PriceTagsState
  scan: Record<string, PriceTagScanState>
  warehouses: StructureResult
  /** Сейчас (ISO) — чтобы сказать, что цены старше суток. */
  now: string
  cameraScanAvailable: boolean
}

const EMPTY_ITEMS: readonly PriceTagItem[] = []

const TIME = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' })
const DATE_TIME = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

const TEMPLATE_OPTIONS = LABEL_TEMPLATES.map((template) => ({
  value: template.id,
  label: `${template.name} · ${template.widthMm}×${template.heightMm} мм`,
}))

export function selectPriceTagSelection(tags: PriceTagsState, resourceId: string): PriceTagSelection | undefined {
  return tags.byResource[normalizeRid(resourceId)]
}

/** Возраст цены: сегодня — время, раньше — дата и время (на ценник уходит снимок). */
function ageOf(oldest: string, now: string): { text: string; old: boolean } {
  const at = new Date(oldest)
  const current = new Date(now)
  if (Number.isNaN(at.getTime())) return { text: oldest, old: true }
  const sameDay = at.toDateString() === current.toDateString()
  return {
    text: sameDay ? TIME.format(at) : DATE_TIME.format(at),
    old: current.getTime() - at.getTime() > DAY_MS,
  }
}

const pickView = (warehouses: StructureResult) => {
  const pick = warehousePickOf(warehouses)
  return { query: pick.query, pick }
}

/** vm окна «Ценники 1С». Мемоизация на экземпляр binding. */
export function makeSelectPriceTagsVm() {
  return createSelector(
    [
      (input: PriceTagsVmInput) => normalizeRid(input.resourceId),
      (input: PriceTagsVmInput) => selectPriceTagSelection(input.tags, input.resourceId),
      (input: PriceTagsVmInput) => input.scan[normalizeRid(input.resourceId)] ?? IDLE_PRICE_TAG_SCAN,
      (input: PriceTagsVmInput) => input.warehouses,
      (input: PriceTagsVmInput) => input.now,
      (input: PriceTagsVmInput) => input.cameraScanAvailable,
    ],
    (resourceId, selection, scan, warehouses, now, cameraScanAvailable): PriceTagsVm => {
      const items = selection?.items ?? EMPTY_ITEMS
      const oldest = items.reduce<string | null>(
        (first, item) => (item.pricedAt === null ? first : first === null || item.pricedAt < first ? item.pricedAt : first),
        null,
      )
      const pricesUnknown = items.filter((item) => item.pricedAt === null).length
      const age = oldest === null ? null : ageOf(oldest, now)
      return {
        resourceId,
        warehouses: { ...pickView(warehouses), selected: selection?.warehouseRefKey ?? '' },
        templates: TEMPLATE_OPTIONS,
        template: templateOf(selection?.templateId ?? ''),
        items: items.map((item) => ({
          productRefKey: item.productRefKey,
          name: item.name.trim() === '' ? EMPTY_NAME_TEXT : item.name,
          barcode: item.barcode,
          price: item.price,
          priceText: item.price === null ? NO_PRICE_TEXT : formatMoney(item.price),
          copies: item.copies,
        })),
        totals: {
          positions: items.length,
          copies: items.reduce((sum, item) => sum + (item.price === null ? 0 : item.copies), 0),
          withoutPrice: items.filter((item) => item.price === null).length,
        },
        pricesAt: age?.text ?? null,
        pricesOld: age?.old ?? false,
        pricesUnknown,
        scan,
        maxCopies: MAX_LABEL_COPIES,
        cameraScanAvailable,
      }
    },
  )
}
